import { formatZoneSize, type DesignerViewSettings } from '@pz/scene';
import { el, note, switchRow } from '../panels.js';
import { fillText } from '../texts.js';
import { phoneIconSvg } from './icons.js';
import { openShareDialog } from '../bottom-bar.js';
import { openSheet, type SheetHandle } from './sheet.js';
import type { PhoneEnv } from './types-phone.js';

/**
 * The ⋯ sheet: the design's name, the six view switches Zazzle's mobile web
 * offers, and the way out of the designer.
 *
 * Zazzle's desktop Settings dialog has eight switches; two of them (mini
 * toolbar, rulers) are desktop-only furniture that a phone never shows, so
 * they are not offered here — the rest are the same switches with the same
 * wording, reading the same `DesignerViewSettings`.
 */
export function openMoreSheet(env: PhoneEnv): SheetHandle {
  const ctx = env.ctx;
  const t = env.texts;

  return openSheet({
    root: env.root,
    title: t.moreTitle,
    closeLabel: t.close,
    build: (body) => {
      // ── Which design this is ────────────────────────────────────────────
      const current = env.surfaces.find((surface) => surface.current);
      const name = el('div', 'pzd-ph-name');
      name.append(el('b', undefined, current?.name || ctx.texts.title));
      name.append(el('small', undefined, fillText(ctx.texts.printSize, { size: formatZoneSize(ctx.config.sizeMm) })));
      const saved = el('small', 'pzd-ph-saved', t.saved);
      name.append(saved);
      body.append(name);
      const stopWatching = env.actions.onSaved((isSaved) => {
        saved.textContent = isSaved ? t.saved : t.saving;
      });

      // ── Where on the product it prints ("Na grudima" / "Džep") ────────────
      if (ctx.printLocations && ctx.switchPrintLocation) {
        body.append(el('span', 'pzd-ph-label', ctx.texts.printLocation));
        const list = el('div', 'pzd-ph-list');
        for (const location of ctx.printLocations) {
          const row = el('button', `pzd-ph-list-btn${location.current ? ' is-on' : ''}`);
          row.type = 'button';
          row.setAttribute('aria-pressed', String(location.current));
          const mark = el('span');
          mark.innerHTML = phoneIconSvg(location.current ? 'check' : 'layers', 18);
          row.append(mark, el('span', undefined, location.name));
          row.addEventListener('click', () => {
            env.closeSheet();
            ctx.switchPrintLocation?.(location.id);
          });
          list.append(row);
        }
        body.append(list, el('div', 'pzd-hint', ctx.texts.printLocationNote));
      }

      // ── The view switches ───────────────────────────────────────────────
      const toggle = (
        key: keyof Pick<DesignerViewSettings, 'snap' | 'guidelines' | 'lockAspect' | 'grid' | 'transparency' | 'darkMode'>,
        label: string,
        hint: string,
      ): void => {
        const row = el('div', 'pzd-ph-toggle');
        row.append(switchRow(label, Boolean(ctx.view[key]), (on) => {
          ctx.setView({ [key]: on } as Partial<DesignerViewSettings>);
          env.syncTop();
        }));
        row.append(el('div', 'pzd-hint', hint));
        body.append(row);
      };
      toggle('snap', t.snap, t.snapNote);
      toggle('guidelines', t.guidelines, t.guidelinesNote);
      toggle('lockAspect', t.lockAspect, t.lockAspectNote);
      toggle('grid', t.gridLines, t.gridLinesNote);
      toggle('transparency', t.transparency, t.transparencyNote);
      toggle('darkMode', t.darkMode, t.darkModeNote);

      // ── The workspace backdrop ──────────────────────────────────────────
      // The canvas draws its own three-circle picker in the corner of the
      // stage, which on a phone lands on top of the design. The node is moved
      // here rather than rebuilt: the canvas keeps repainting it, so a copy
      // would go stale the moment the backdrop changed.
      const picker = ctx.root.querySelector<HTMLElement>('.pzd-backdrop-pick');
      const home = picker?.parentElement ?? null;
      const after = picker?.nextElementSibling ?? null;
      if (picker && home) {
        body.append(el('span', 'pzd-ph-label', ctx.texts.backdrop));
        body.append(picker);
      }
      const returnPicker = (): void => {
        if (!picker || !home) return;
        if (after && after.parentElement === home) home.insertBefore(picker, after);
        else home.append(picker);
      };

      // ── Keyboard help, only where there is a keyboard ────────────────────
      if (!ctx.root.classList.contains('is-touch')) {
        body.append(el('span', 'pzd-ph-label', t.shortcuts));
        body.append(note(t.shortcutsBody));
      }

      // Saved designs and sharing: the desktop's share dialog, opened from here.
      if (ctx.share || ctx.library) {
        const saved = el('button', 'pzd-ph-secondary', ctx.library ? ctx.texts.libraryTitle : ctx.texts.shareDesign);
        saved.type = 'button';
        saved.addEventListener('click', () => {
          env.closeSheet();
          openShareDialog(ctx);
        });
        body.append(saved);
      }

      const close = el('button', 'pzd-ph-secondary', t.closeDesigner);
      close.type = 'button';
      close.addEventListener('click', () => {
        env.closeSheet();
        env.actions.cancel();
      });
      body.append(close);

      return () => {
        returnPicker();
        stopWatching();
      };
    },
  });
}

/**
 * The side picker behind "Prednja strana ▾" — the label Zazzle's app puts in
 * the middle of the top bar. Switching commits the current side first, which
 * the engine's `switchSurface` already does.
 */
export function openSidesSheet(env: PhoneEnv): SheetHandle {
  return openSheet({
    root: env.root,
    title: env.texts.sideTitle,
    closeLabel: env.texts.close,
    build: (body) => {
      const list = el('div', 'pzd-ph-list');
      for (const surface of env.surfaces) {
        const row = el('button', `pzd-ph-list-btn${surface.current ? ' is-on' : ''}`);
        row.type = 'button';
        row.setAttribute('aria-label', surface.name);
        row.setAttribute('aria-pressed', String(surface.current));
        const mark = el('span');
        mark.innerHTML = phoneIconSvg(surface.current ? 'check' : 'layers', 18);
        row.append(mark, el('span', undefined, surface.name));
        row.addEventListener('click', () => {
          env.closeSheet();
          if (!surface.current) env.switchSurface?.(surface.id);
        });
        list.append(row);
      }
      body.append(list);
    },
  });
}
